import React, { useEffect, useReducer, useState } from 'react';
import { BaseModal } from '@repo/components/modals';
import cn from 'classnames';
import Header from '@components/Modals/Header';
import styles from './CSV.module.scss';
import type { ParseResult } from 'papaparse';
import Papa from 'papaparse';
import { ChevronRightIcon } from '@repo/icons';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { createCSVImport, ImportablePerson } from '@repo/redux/actions/csvImport';
import { useDispatch, useSelector } from 'react-redux';
import DoneView from './panels/Done';
import UploadView from './panels/Upload';
import ImportView from './panels/Import';
import ReviewView from './panels/Review';
import { clayAddedFields, CSVRecord, EMAIL, GROUPS, NOTE, PHONE, supportedFields } from './vars';
import { includesLookup, validateRow } from './validations';
import { selectImportStatusByLocalID } from '@repo/redux/reducers/data/imports';

const supportedDelimiters = [',', ';', '\n', '\t', ':::'];

async function openAndParseCSVFile(file: File): Promise<ParseResult<CSVRecord>> {
  const detectedDelimiter = {
    [EMAIL]: undefined,
    [PHONE]: undefined,
    [GROUPS]: undefined,
  };

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: resolve,
      error: reject,
      // 'greedy' skips lines that just have whitespace
      skipEmptyLines: 'greedy',
      transform: (value, userField: string) => {
        const field = userField.toLowerCase().trim();
        if ([EMAIL, PHONE, GROUPS].includes(field)) {
          if (!detectedDelimiter[field]) {
            detectedDelimiter[field] = supportedDelimiters.find((delimiter) =>
              value.includes(delimiter),
            );
          }

          let values = [];

          if (detectedDelimiter[field]) {
            values = value.split(detectedDelimiter[field]).map((v) => v.trim());
          } else {
            values = [value.trim()];
          }

          return values.filter((v) => !!v);
        }

        if ([NOTE].includes(field) && value) {
          return [value];
        }

        return String(value).trim();
      },
    });
  });
}

type CSVModalView = 'upload' | 'review' | 'import' | 'done';

interface CSVModalState {
  userFile?: File;
  sanitizedRecords?: ImportablePerson[];
  CSVWithErrorStr?: string;
  erroredRowCount?: number;
  warnedRowCount?: number;
  detectedUnsupportedFields?: string[];
  hasImportableHeaders?: boolean;
  hasNoHeadersFromTemplate?: boolean;
  allGroupNames?: string[];
  localImportID?: string;
  errorCreatingImport?: boolean;
  isParsing?: boolean;
  parsingError?: boolean;
  view: CSVModalView;
}

type CSVModalStateAction =
  | ['restart']
  | ['fileSelectionComplete', { file: File }]
  | ['parsingStarted']
  | [
      'parsingComplete',
      {
        sanitizedRecords: ImportablePerson[];
        erroredRowCount: number;
        warnedRowCount: number;
        detectedUnsupportedFields: string[];
        CSVWithErrorStr?: string;
        hasImportableHeaders: boolean;
        hasNoHeadersFromTemplate: boolean;
        allGroupNames: string[];
      },
    ]
  | ['parsingFailed']
  | ['returnToReview']
  | ['readyToImport']
  | ['createImportStarted', { localImportID: string }]
  | ['createImportComplete']
  | ['createImportFailed'];

const csvModalStateReducer = (state: CSVModalState, action: CSVModalStateAction): CSVModalState => {
  switch (action[0]) {
    case 'restart':
      return {
        ...state,
        view: 'upload',
      };

    case 'fileSelectionComplete':
      return {
        ...state,
        userFile: action[1].file,
      };

    case 'parsingStarted':
      return {
        ...state,
        isParsing: true,
        parsingError: false,
      };

    case 'parsingComplete':
      return {
        ...state,
        sanitizedRecords: action[1].sanitizedRecords,
        erroredRowCount: action[1].erroredRowCount,
        warnedRowCount: action[1].warnedRowCount,
        detectedUnsupportedFields: action[1].detectedUnsupportedFields,
        CSVWithErrorStr: action[1].CSVWithErrorStr,
        hasImportableHeaders: action[1].hasImportableHeaders,
        hasNoHeadersFromTemplate: action[1].hasNoHeadersFromTemplate,
        allGroupNames: action[1].allGroupNames,
        view: 'review',
        isParsing: false,
      };

    case 'parsingFailed':
      return {
        ...state,
        parsingError: true,
        isParsing: false,
      };

    case 'returnToReview':
      return {
        ...state,
        view: 'review',
      };

    case 'readyToImport':
      return {
        ...state,
        view: 'import',
      };

    case 'createImportStarted':
      return {
        ...state,
        localImportID: action[1].localImportID,
        errorCreatingImport: false,
      };

    case 'createImportComplete':
      return {
        ...state,
        view: 'done',
      };

    case 'createImportFailed':
      return {
        ...state,
        errorCreatingImport: true,
      };

    default:
      return state;
  }
};

export default function CSVModal({ onClose, forceDarkMode = false, isInOnboarding = false }) {
  const dispatchToStore = useDispatch();
  const [
    {
      userFile,
      isParsing,
      parsingError,
      sanitizedRecords,
      CSVWithErrorStr,
      erroredRowCount,
      warnedRowCount,
      detectedUnsupportedFields,
      view,
      hasImportableHeaders,
      hasNoHeadersFromTemplate,
      allGroupNames,
      localImportID,
      errorCreatingImport,
    },
    dispatch,
  ] = useReducer(csvModalStateReducer, {
    view: 'upload',
  });
  const [downloadableCSV, setDownloadableCSV] = useState<string>();
  const importStatus = useSelector(selectImportStatusByLocalID(localImportID));
  const hasColumnError = !hasImportableHeaders || hasNoHeadersFromTemplate;
  const hasColumnWarning = detectedUnsupportedFields?.length > 0;
  const hasRowError = erroredRowCount > 0;
  const hasRowWarning = warnedRowCount > 0;
  const hasNoRows = !sanitizedRecords || sanitizedRecords.length === 0;
  const hasNoImportableRows = erroredRowCount === sanitizedRecords?.length;

  const startImport = () => {
    const localImportID = Date.now().toString();

    dispatchToStore(
      createCSVImport({
        rows: sanitizedRecords,
        group_names: allGroupNames,
        localImportID,
      }),
    );

    dispatch(['createImportStarted', { localImportID }]);
  };

  useEffect(() => {
    if (!userFile || !isParsing) {
      return void 0;
    }

    const abort = new AbortController();

    openAndParseCSVFile(userFile)
      .then((parsed) => {
        const detectedFields = parsed.meta.fields
          .map((field) => field.toLowerCase())
          .filter((field) => !clayAddedFields.includes(field));
        const detectedUnsupportedFields = detectedFields.filter(
          (field) => !supportedFields.includes(field),
        );
        const hasImportableHeaders = includesLookup(detectedFields);
        let erroredRowCount = 0;
        let warnedRowCount = 0;

        const [importableRows, csvRecordsWithErrors, allGroupNames] = parsed.data.reduce(
          ([_importableRows, _csvRecordsWithErrors, _allGroupNames], record) => {
            const { importable, sanitizedRecord, recordWithErrors, groupNames } =
              validateRow(record);

            if (!importable) {
              erroredRowCount += 1;
              return [
                _importableRows,
                [..._csvRecordsWithErrors, recordWithErrors],
                _allGroupNames,
              ];
            }

            if (recordWithErrors._warnings) {
              warnedRowCount += 1;
            }

            return [
              [..._importableRows, sanitizedRecord],
              [..._csvRecordsWithErrors, recordWithErrors],
              Array.from(new Set([..._allGroupNames, ...groupNames])),
            ];
          },
          [[], [], []],
        );

        const CSVWithErrorStr = Papa.unparse({
          fields: parsed.meta.fields
            .filter((field) => !clayAddedFields.includes(field))
            .concat(clayAddedFields),
          data: csvRecordsWithErrors,
        });

        dispatch([
          'parsingComplete',
          {
            sanitizedRecords: importableRows,
            erroredRowCount,
            warnedRowCount,
            detectedUnsupportedFields,
            CSVWithErrorStr,
            hasImportableHeaders,
            hasNoHeadersFromTemplate: detectedFields.length === detectedUnsupportedFields.length,
            allGroupNames,
          },
        ]);
      })
      .catch((e) => {
        console.error(e);
        dispatch(['parsingFailed']);
      });

    return () => {
      abort.abort();
    };
  }, [userFile, isParsing]);

  useEffect(() => {
    if (CSVWithErrorStr) {
      const blob = new Blob([CSVWithErrorStr], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      setDownloadableCSV(url);
    }
  }, [CSVWithErrorStr]);

  useEffect(() => {
    if (localImportID && importStatus?.groupID) {
      dispatch(['createImportComplete']);
    }
  }, [importStatus, localImportID]);

  useEffect(() => {
    if (importStatus?.error) {
      dispatch(['createImportFailed']);
    }
  }, [importStatus]);

  return (
    <BaseModal
      className={cn(styles.csvModal, 'modal-medium', view === 'done' && styles.finalView)}
      forceDarkMode={forceDarkMode}
      centered
      isOpen={true}
      onRequestClose={onClose}
    >
      <StateBasedFadeTransition
        state={String(view === 'done')}
        elementByStateMap={{
          true: (
            <div>
              <DoneView
                close={onClose}
                forceDarkMode={forceDarkMode}
                groupID={importStatus?.groupID}
                isInOnboarding={isInOnboarding}
              />
            </div>
          ),
          false: (
            <div>
              <Header className={styles.header} v2>
                <span className={cn(view === 'upload' && styles.active)}>Upload file</span>
                <ChevronRightIcon width="5" strokeWidth="2" color="textPrimary" />
                <span className={cn(view === 'review' && styles.active)}>Review</span>
                <ChevronRightIcon width="5" strokeWidth="2" color="textPrimary" />
                <span className={cn(view === 'import' && styles.active)}>Import</span>
              </Header>
              <StateBasedFadeTransition
                state={view as 'upload' | 'review' | 'import'}
                elementByStateMap={{
                  upload: (
                    <div className={cn(styles.csvContent, 'upload')}>
                      <UploadView
                        selectFile={(file) => dispatch(['fileSelectionComplete', { file }])}
                        closeModal={onClose}
                        upload={() => dispatch(['parsingStarted'])}
                        uploadDisabled={!userFile}
                        forceDarkMode={forceDarkMode}
                        isParsing={isParsing}
                        parsingError={parsingError}
                      />
                    </div>
                  ),
                  review: (
                    <div className={cn(styles.csvContent, 'review')}>
                      <ReviewView
                        errors={{ row: hasRowError, column: hasColumnError }}
                        warnings={{ row: hasRowWarning, column: hasColumnWarning }}
                        fileName={userFile?.name}
                        backToUpload={() => dispatch(['restart'])}
                        hasNoHeadersFromTemplate={hasNoHeadersFromTemplate}
                        hasImportableHeaders={hasImportableHeaders}
                        detectedUnsupportedFields={detectedUnsupportedFields}
                        erroredRowCount={erroredRowCount}
                        warnedRowCount={warnedRowCount}
                        importableRowCount={sanitizedRecords?.length}
                        hasNoRows={hasNoRows}
                        hasNoImportableRows={hasNoImportableRows}
                        downloadableCSV={downloadableCSV}
                        proceedToImportStep={() => dispatch(['readyToImport'])}
                        forceDarkMode={forceDarkMode}
                      />
                    </div>
                  ),
                  import: (
                    <div className={cn(styles.csvContent, 'import')}>
                      <ImportView
                        forceDarkMode={forceDarkMode}
                        startImport={startImport}
                        erroredRowCount={erroredRowCount}
                        warnedRowCount={warnedRowCount}
                        importableRowCount={sanitizedRecords?.length}
                        goBack={() => dispatch(['returnToReview'])}
                        errorCreatingImport={errorCreatingImport}
                      />
                    </div>
                  ),
                }}
              />
            </div>
          ),
        }}
      />
    </BaseModal>
  );
}
